import { useState, useEffect, useCallback } from "react";
import { FileText, Upload, CheckCircle, XCircle, Clock, AlertTriangle, DollarSign, Download, Loader2, Send, Ban, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatMoney, currencySymbol } from "@/lib/money";

const statusConfig = {
  draft: { label: "Draft", icon: FileText, color: "text-slate-500", bg: "bg-slate-100" },
  requested: { label: "Requested", icon: Clock, color: "text-amber-600", bg: "bg-amber-50" },
  waiting_for_payment: { label: "Waiting for payment", icon: AlertTriangle, color: "text-blue-600", bg: "bg-blue-50" },
  paid: { label: "Paid", icon: CheckCircle, color: "text-emerald-600", bg: "bg-emerald-50" },
  cancelled: { label: "Cancelled", icon: XCircle, color: "text-red-500", bg: "bg-red-50" },
};

function StatusBadge({ status }) {
  const config = statusConfig[status] || statusConfig.draft;
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${config.bg} ${config.color}`}>
      <Icon size={12} /> {config.label}
    </span>
  );
}

export default function BillingPage({ userRole, userId }) {
  const [tab, setTab] = useState("billing");

  return (
    <div className="p-6 md:p-8" data-testid="billing-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          Billing
        </h1>
        <p className="text-sm text-slate-500 mt-1">Manage invoices and payment history</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6 bg-slate-100 p-1 rounded-lg" data-testid="billing-tabs">
          <TabsTrigger value="billing" className="text-sm px-5 data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-md" data-testid="billing-tab-billing">
            Billing
          </TabsTrigger>
          <TabsTrigger value="history" className="text-sm px-5 data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-md" data-testid="billing-tab-history">
            History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="billing" className="mt-0">
          <BillingTab userRole={userRole} userId={userId} />
        </TabsContent>
        <TabsContent value="history" className="mt-0">
          <HistoryTab userRole={userRole} userId={userId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function BillingTab({ userRole, userId }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [partners, setPartners] = useState([]);
  const [partnerFilter, setPartnerFilter] = useState("all");
  const [generateMonth, setGenerateMonth] = useState("");
  const [generating, setGenerating] = useState(false);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 20;
  const isAdmin = userRole === "admin" || userRole === "user";

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (isAdmin && partnerFilter && partnerFilter !== "all") params.partner_id = partnerFilter;
      const res = await api.get("/billing/invoices", { params });
      setInvoices(res.data);
    } catch { toast.error("Failed to load invoices"); }
    setLoading(false);
  }, [isAdmin, partnerFilter]);

  const fetchPartners = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await api.get("/billing/partners-list");
      setPartners(res.data);
    } catch {}
  }, [isAdmin]);

  useEffect(() => { fetchPartners(); }, [fetchPartners]);
  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const handleGenerate = async () => {
    if (!generateMonth) { toast.error("Enter a month (YYYY-MM)"); return; }
    setGenerating(true);
    try {
      const res = await api.post("/billing/invoices/generate", { month: generateMonth });
      toast.success(res.data.message);
      fetchInvoices();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to generate"); }
    setGenerating(false);
  };

  const handlePublish = async (invoiceId) => {
    try {
      await api.put(`/billing/invoices/${invoiceId}/publish`);
      toast.success("Invoice published");
      fetchInvoices();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to publish"); }
  };

  const handleCancel = async (invoiceId) => {
    try {
      await api.put(`/billing/invoices/${invoiceId}/cancel`);
      toast.success("Invoice cancelled");
      fetchInvoices();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to cancel"); }
  };

  return (
    <div className="space-y-5">
      {/* Admin: Generate + Filter */}
      {isAdmin && (
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-500">Generate Invoices</Label>
            <div className="flex gap-2">
              <Input value={generateMonth} onChange={(e) => setGenerateMonth(e.target.value)} placeholder="YYYY-MM" className="h-9 w-32 border-slate-200 text-sm" data-testid="generate-month-input" />
              <Button onClick={handleGenerate} disabled={generating} size="sm" className="h-9 bg-blue-600 hover:bg-blue-700 text-white" data-testid="generate-invoices-btn">
                {generating ? <Loader2 size={14} className="animate-spin mr-1" /> : <FileText size={14} className="mr-1" />}
                Generate
              </Button>
            </div>
          </div>
          <div className="space-y-1.5 ml-auto">
            <Label className="text-xs text-slate-500">Filter by Partner</Label>
            <Select value={partnerFilter} onValueChange={setPartnerFilter}>
              <SelectTrigger className="w-[200px] h-9 border-slate-200 text-sm" data-testid="billing-partner-filter">
                <SelectValue placeholder="All Partners" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Partners</SelectItem>
                {partners.map((p) => (
                  <SelectItem key={p.partner_id} value={p.partner_id}>{p.partner_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* Invoices Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="invoices-table">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Status</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">No.</TableHead>
              {isAdmin && <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Partner</TableHead>}
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Month</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4 text-right">Amount</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Request Date</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Upload Date</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Paid Date</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={isAdmin ? 9 : 8} className="text-center py-12"><Loader2 className="animate-spin text-blue-600 mx-auto" size={24} /></TableCell></TableRow>
            ) : invoices.length === 0 ? (
              <TableRow><TableCell colSpan={isAdmin ? 9 : 8} className="text-center py-12 text-slate-400">No invoices found</TableCell></TableRow>
            ) : (
              invoices.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map((inv) => (
                <TableRow key={inv.invoice_id} className="border-b border-slate-100 hover:bg-slate-50/50">
                  <TableCell className="py-3 px-4"><StatusBadge status={inv.status} /></TableCell>
                  <TableCell className="py-3 px-4 text-sm font-mono text-slate-700">{inv.invoice_number}</TableCell>
                  {isAdmin && <TableCell className="py-3 px-4 text-sm text-slate-700">{inv.partner_name}</TableCell>}
                  <TableCell className="py-3 px-4 text-sm text-slate-700">{inv.month}</TableCell>
                  <TableCell className="py-3 px-4 text-right">
                    {(() => {
                      const cur = inv.currency || "USD";
                      const addl = inv.additional_cost || 0;
                      const net = inv.net_amount ?? inv.amount;
                      const pendingInr = cur === "INR" && inv.usd_amount != null && inv.amount_billed == null;
                      if (cur === "INR" && inv.amount_billed != null) {
                        return (
                          <>
                            <div className="text-sm font-mono font-semibold text-slate-900" data-testid={`amount-${inv.invoice_id}`}>{formatMoney(inv.amount_billed, "INR")}</div>
                            {inv.fx_rate && <div className="text-[10px] text-slate-400 font-mono">{formatMoney(inv.usd_amount, "USD")} × {inv.fx_rate}</div>}
                          </>
                        );
                      }
                      if (pendingInr) {
                        return (
                          <>
                            <div className="text-sm font-mono font-semibold text-slate-900" data-testid={`amount-${inv.invoice_id}`}>{formatMoney(inv.usd_amount, "USD")}</div>
                            <div className="text-[10px] text-amber-600 font-mono">→ ₹ at settlement</div>
                          </>
                        );
                      }
                      return (
                        <>
                          <div className="text-sm font-mono font-semibold text-slate-900" data-testid={`amount-${inv.invoice_id}`}>{formatMoney(net, "USD")}</div>
                          {addl > 0 && (
                            <div className="text-[10px] text-slate-400 font-mono" data-testid={`amount-breakdown-${inv.invoice_id}`}>gross {formatMoney(inv.amount, "USD")} · −{formatMoney(addl, "USD")} cost</div>
                          )}
                        </>
                      );
                    })()}
                  </TableCell>
                  <TableCell className="py-3 px-4 text-xs text-slate-500">{inv.publish_date ? new Date(inv.publish_date).toLocaleDateString() : "—"}</TableCell>
                  <TableCell className="py-3 px-4 text-xs text-slate-500">{inv.upload_date ? new Date(inv.upload_date).toLocaleDateString() : "—"}</TableCell>
                  <TableCell className="py-3 px-4 text-xs text-slate-500">{inv.paid_date ? new Date(inv.paid_date).toLocaleDateString() : "—"}</TableCell>
                  <TableCell className="py-3 px-4">
                    <InvoiceActions invoice={inv} userRole={userRole} onRefresh={fetchInvoices} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      {/* Pagination */}
      {invoices.length > PAGE_SIZE && (
        <div className="flex items-center justify-between" data-testid="billing-pagination">
          <span className="text-xs text-slate-500">
            {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, invoices.length)} of {invoices.length}
          </span>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)} className="h-8 w-8 p-0">
              <ChevronLeft size={16} />
            </Button>
            <span className="text-xs text-slate-600 px-2">{page + 1} / {Math.ceil(invoices.length / PAGE_SIZE)}</span>
            <Button variant="outline" size="sm" disabled={(page + 1) * PAGE_SIZE >= invoices.length} onClick={() => setPage(page + 1)} className="h-8 w-8 p-0">
              <ChevronRight size={16} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ReconcileBadge({ invoice }) {
  const [rec, setRec] = useState(null);
  const [loading, setLoading] = useState(false);
  const check = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/billing/invoices/${invoice.invoice_id}/reconciliation`);
      setRec(data);
    } catch (e) { toast.error("Could not reconcile"); }
    setLoading(false);
  };
  if (!rec) {
    return (
      <Button size="sm" variant="ghost" onClick={check} disabled={loading} className="h-7 text-xs px-2 text-slate-500 hover:bg-slate-100" data-testid={`reconcile-${invoice.invoice_id}`}>
        {loading ? <Loader2 size={12} className="animate-spin" /> : <><AlertTriangle size={12} className="mr-1" /> Check</>}
      </Button>
    );
  }
  if (!rec.diverged) {
    return <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-600" data-testid={`reconcile-ok-${invoice.invoice_id}`}><CheckCircle size={12} /> In sync</span>;
  }
  const sign = rec.delta_base > 0 ? "+" : "";
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-600" title={`Billed ${formatMoney(rec.billed_base, "USD")} · Live ${formatMoney(rec.live_base, "USD")}`} data-testid={`reconcile-diverged-${invoice.invoice_id}`}>
      <AlertTriangle size={12} /> Δ {sign}{formatMoney(rec.delta_base, "USD")}
    </span>
  );
}

function InvoiceActions({ invoice, userRole, onRefresh }) {
  const isAdmin = userRole === "admin" || userRole === "user";
  const [uploading, setUploading] = useState(false);
  const [paying, setPaying] = useState(false);
  const [payAmount, setPayAmount] = useState((invoice.net_amount ?? invoice.amount)?.toString() || "");
  const [payFile, setPayFile] = useState(null);
  const [payRate, setPayRate] = useState("");
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const needsRate = (invoice.currency === "INR") && invoice.usd_amount != null && invoice.amount_billed == null;
  const [publishOpen, setPublishOpen] = useState(false);
  const [addlCost, setAddlCost] = useState("0");
  const [addlNote, setAddlNote] = useState("");
  const [publishing, setPublishing] = useState(false);

  const handleUploadInvoice = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await api.put(`/billing/invoices/${invoice.invoice_id}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Invoice uploaded");
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    setUploading(false);
  };

  const handlePay = async () => {
    if (!payAmount) { toast.error("Enter payment amount"); return; }
    if (needsRate && (!payRate || parseFloat(payRate) <= 0)) { toast.error("Enter the USD→INR rate"); return; }
    setPaying(true);
    try {
      const formData = new FormData();
      formData.append("paid_amount", parseFloat(payAmount));
      if (needsRate) formData.append("fx_rate", parseFloat(payRate));
      if (payFile) formData.append("file", payFile);
      await api.put(`/billing/invoices/${invoice.invoice_id}/pay`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Invoice marked as paid");
      setPayDialogOpen(false);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setPaying(false);
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      await api.put(`/billing/invoices/${invoice.invoice_id}/publish`, {
        additional_cost: parseFloat(addlCost) || 0,
        additional_cost_note: addlNote || null,
      });
      toast.success("Invoice published");
      setPublishOpen(false);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setPublishing(false);
  };

  const handleCancel = async () => {
    try {
      await api.put(`/billing/invoices/${invoice.invoice_id}/cancel`);
      toast.success("Invoice cancelled");
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  // Admin actions
  if (isAdmin) {
    if (invoice.status === "draft") {
      const cost = parseFloat(addlCost) || 0;
      const net = (invoice.amount || 0) - cost;
      return (
        <div className="flex gap-1.5">
          <Dialog open={publishOpen} onOpenChange={setPublishOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" className="h-7 text-xs px-2 hover:bg-blue-50 hover:text-blue-600" data-testid={`publish-${invoice.invoice_id}`}>
                <Send size={12} className="mr-1" /> Publish
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader><DialogTitle className="text-lg">Publish Invoice</DialogTitle></DialogHeader>
              <div className="space-y-4 pt-2">
                <div className="text-sm text-slate-500">Invoice <span className="font-mono font-semibold text-slate-900">{invoice.invoice_number}</span> for <span className="font-semibold text-slate-900">{invoice.partner_name}</span></div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Computed payout (net of ad cost)</span>
                  <span className="font-mono font-semibold text-slate-900">${invoice.amount?.toFixed(2)}</span>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Additional Cost ($) <span className="text-slate-400 font-normal">— deducted from payout</span></Label>
                  <Input type="number" step="0.01" min="0" value={addlCost} onChange={(e) => setAddlCost(e.target.value)} className="h-10 border-slate-200" data-testid="publish-additional-cost" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Note (optional)</Label>
                  <Input value={addlNote} onChange={(e) => setAddlNote(e.target.value)} placeholder="Reason for additional cost" className="h-10 border-slate-200" data-testid="publish-cost-note" />
                </div>
                <div className="flex items-center justify-between text-sm border-t border-slate-100 pt-3">
                  <span className="font-medium text-slate-700">Net payable to partner</span>
                  <span className={`font-mono font-bold ${net < 0 ? "text-red-600" : "text-emerald-700"}`} data-testid="publish-net-preview">${net.toFixed(2)}</span>
                </div>
                {net < 0 && <p className="text-xs text-red-600">Additional cost cannot exceed the payout amount.</p>}
                <Button onClick={handlePublish} disabled={publishing || net < 0} className="w-full h-10 bg-blue-600 hover:bg-blue-700 text-white" data-testid="confirm-publish-btn">
                  {publishing ? "Publishing..." : "Publish Invoice"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          <Button size="sm" variant="ghost" onClick={handleCancel} className="h-7 text-xs px-2 text-red-500 hover:bg-red-50" data-testid={`cancel-${invoice.invoice_id}`}>
            <Ban size={12} />
          </Button>
        </div>
      );
    }
    if (invoice.status === "waiting_for_payment") {
      return (
        <div className="flex gap-1.5">
          {invoice.invoice_file_url && (
            <a href={`${process.env.REACT_APP_BACKEND_URL}${invoice.invoice_file_url}`} target="_blank" rel="noreferrer">
              <Button size="sm" variant="ghost" className="h-7 text-xs px-2 text-blue-600 hover:bg-blue-50" data-testid={`view-invoice-${invoice.invoice_id}`}>
                <Download size={12} className="mr-1" /> View
              </Button>
            </a>
          )}
          <Dialog open={payDialogOpen} onOpenChange={setPayDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="h-7 text-xs px-3 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`pay-${invoice.invoice_id}`}>
                <DollarSign size={12} className="mr-1" /> Mark Paid
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader><DialogTitle className="text-lg">Mark Invoice as Paid</DialogTitle></DialogHeader>
              <div className="space-y-4 pt-2">
                <div className="text-sm text-slate-500">Invoice <span className="font-mono font-semibold text-slate-900">{invoice.invoice_number}</span> for <span className="font-semibold text-slate-900">{invoice.partner_name}</span></div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Net payable{(invoice.additional_cost || 0) > 0 ? " (after additional cost)" : ""}</span>
                  <span className="font-mono font-semibold text-slate-900">{formatMoney(invoice.usd_amount ?? invoice.net_amount ?? invoice.amount, needsRate ? "USD" : (invoice.currency || "USD"))}</span>
                </div>
                {needsRate && (
                  <div className="space-y-2 bg-amber-50 border border-amber-100 rounded-md p-3">
                    <Label className="text-sm text-amber-800">USD → INR rate (payment-date bank rate)</Label>
                    <Input type="number" step="0.0001" min="0" value={payRate} onChange={(e) => setPayRate(e.target.value)} placeholder="e.g. 83.5" className="h-10 border-amber-200 bg-white" data-testid="pay-fx-rate-input" />
                    <div className="flex items-center justify-between text-sm pt-1">
                      <span className="text-amber-800">Amount to bill (₹)</span>
                      <span className="font-mono font-bold text-amber-900" data-testid="pay-inr-preview">
                        {payRate && parseFloat(payRate) > 0 ? formatMoney((invoice.usd_amount || 0) * parseFloat(payRate), "INR") : "—"}
                      </span>
                    </div>
                    <p className="text-[11px] text-amber-600">This locks the ₹ amount and freezes this partner-month. Past dashboard numbers won't change afterwards.</p>
                  </div>
                )}
                <div className="space-y-2">
                  <Label className="text-sm">Payment Amount ({currencySymbol(needsRate ? "INR" : (invoice.currency || "USD"))})</Label>
                  <Input type="number" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} className="h-10 border-slate-200" data-testid="pay-amount-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Payment Confirmation (optional)</Label>
                  <Input type="file" onChange={(e) => setPayFile(e.target.files?.[0])} className="h-10 border-slate-200" data-testid="pay-confirmation-file" />
                </div>
                <Button onClick={handlePay} disabled={paying} className="w-full h-10 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="confirm-pay-btn">
                  {paying ? "Processing..." : "Confirm Payment"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          <Button size="sm" variant="ghost" onClick={handleCancel} className="h-7 text-xs px-2 text-red-500 hover:bg-red-50" data-testid={`cancel-${invoice.invoice_id}`}>
            <Ban size={12} />
          </Button>
        </div>
      );
    }
    if (invoice.status === "requested") {
      return (
        <Button size="sm" variant="ghost" onClick={handleCancel} className="h-7 text-xs px-2 text-red-500 hover:bg-red-50" data-testid={`cancel-${invoice.invoice_id}`}>
          <Ban size={12} className="mr-1" /> Cancel
        </Button>
      );
    }
    if (invoice.status === "paid") {
      return (
        <div className="flex items-center gap-1.5">
          <ReconcileBadge invoice={invoice} />
          {invoice.payment_confirmation_url && (
            <a href={`${process.env.REACT_APP_BACKEND_URL}${invoice.payment_confirmation_url}`} target="_blank" rel="noreferrer">
              <Button size="sm" variant="ghost" className="h-7 text-xs px-2 text-blue-600 hover:bg-blue-50">
                <Download size={12} className="mr-1" /> Receipt
              </Button>
            </a>
          )}
        </div>
      );
    }
    return <span className="text-xs text-slate-400">—</span>;
  }

  // Partner actions
  if (invoice.status === "requested") {
    return (
      <div className="relative">
        <input type="file" className="absolute inset-0 opacity-0 cursor-pointer" onChange={handleUploadInvoice} disabled={uploading} data-testid={`upload-invoice-${invoice.invoice_id}`} />
        <Button size="sm" variant="outline" className="h-7 text-xs px-3 hover:bg-blue-50 hover:text-blue-600" disabled={uploading}>
          {uploading ? <Loader2 size={12} className="animate-spin mr-1" /> : <Upload size={12} className="mr-1" />}
          Upload Invoice
        </Button>
      </div>
    );
  }
  if (invoice.status === "paid" && invoice.payment_confirmation_url) {
    return (
      <a href={`${process.env.REACT_APP_BACKEND_URL}${invoice.payment_confirmation_url}`} target="_blank" rel="noreferrer">
        <Button size="sm" variant="ghost" className="h-7 text-xs px-2 text-blue-600 hover:bg-blue-50">
          <Download size={12} className="mr-1" /> Receipt
        </Button>
      </a>
    );
  }
  return <span className="text-xs text-slate-400">—</span>;
}

function HistoryTab({ userRole, userId }) {
  const [data, setData] = useState({ transactions: [], balance: 0, unpaid_balance: 0 });
  const [loading, setLoading] = useState(true);
  const [partners, setPartners] = useState([]);
  const [partnerFilter, setPartnerFilter] = useState("all");
  const isAdmin = userRole === "admin" || userRole === "user";

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (isAdmin && partnerFilter && partnerFilter !== "all") params.partner_id = partnerFilter;
      const res = await api.get("/billing/history", { params });
      setData(res.data);
    } catch { toast.error("Failed to load history"); }
    setLoading(false);
  }, [isAdmin, partnerFilter]);

  const fetchPartners = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await api.get("/billing/partners-list");
      setPartners(res.data);
    } catch {}
  }, [isAdmin]);

  useEffect(() => { fetchPartners(); }, [fetchPartners]);
  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  return (
    <div className="space-y-5">
      {/* Balance Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="balance-card">
          <div className="text-xs text-slate-400 mb-1">Balance</div>
          <div className={`text-2xl font-bold font-mono ${data.balance >= 0 ? "text-emerald-700" : "text-red-600"}`}>
            ${Math.abs(data.balance).toFixed(2)}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="unpaid-balance-card">
          <div className="text-xs text-slate-400 mb-1">Unpaid Balance</div>
          <div className="text-2xl font-bold font-mono text-amber-600">
            ${data.unpaid_balance?.toFixed(2)}
          </div>
        </div>
      </div>

      {/* Partner Filter */}
      {isAdmin && (
        <div className="flex justify-end">
          <Select value={partnerFilter} onValueChange={setPartnerFilter}>
            <SelectTrigger className="w-[200px] h-9 border-slate-200 text-sm" data-testid="history-partner-filter">
              <SelectValue placeholder="All Partners" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Partners</SelectItem>
              {partners.map((p) => (
                <SelectItem key={p.partner_id} value={p.partner_id}>{p.partner_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Transactions Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="history-table">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Date</TableHead>
              {isAdmin && <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Partner</TableHead>}
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Details</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Status</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4 text-right">Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={isAdmin ? 5 : 4} className="text-center py-12"><Loader2 className="animate-spin text-blue-600 mx-auto" size={24} /></TableCell></TableRow>
            ) : data.transactions.length === 0 ? (
              <TableRow><TableCell colSpan={isAdmin ? 5 : 4} className="text-center py-12 text-slate-400">No transactions yet</TableCell></TableRow>
            ) : (
              data.transactions.map((txn) => (
                <TableRow key={txn.transaction_id} className="border-b border-slate-100 hover:bg-slate-50/50">
                  <TableCell className="py-3 px-4 text-sm text-slate-700">{txn.date}</TableCell>
                  {isAdmin && <TableCell className="py-3 px-4 text-sm text-slate-700">{txn.partner_name}</TableCell>}
                  <TableCell className="py-3 px-4">
                    <div className="text-sm text-slate-700">
                      {txn.type === "monthly_bill" ? "Monthly bill" : "Funds transfer"}
                    </div>
                    <div className="text-xs text-slate-400">{txn.details}</div>
                  </TableCell>
                  <TableCell className="py-3 px-4">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${txn.status === "paid" ? "bg-emerald-50 text-emerald-600" : txn.status === "cancelled" ? "bg-red-50 text-red-500" : "bg-amber-50 text-amber-600"}`}>
                      {txn.status}
                    </span>
                  </TableCell>
                  <TableCell className={`py-3 px-4 text-sm text-right font-mono font-semibold ${txn.amount >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                    {txn.amount >= 0 ? "+" : ""}{txn.amount < 0 ? "-" : ""}${Math.abs(txn.amount).toFixed(2)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
